import { useCallback, useEffect, useState } from 'react';
import { api, isSignedIn, storeSession, type RoundSummary, type Session } from './api.js';
import { formatCountdown, formatTzs } from './money.js';
import { Betslip } from './Betslip.js';
import { Login } from './Login.js';
import { Wallet } from './Wallet.js';
import { MyTickets } from './MyTickets.js';
import { S } from './ui.js';

type Tab = 'rounds' | 'tickets' | 'wallet' | 'account';

/**
 * App shell.
 *
 * Routing is two pieces of state rather than a router library — spec §7 budgets
 * under 150KB of JS on first load, and at this screen count a router costs more
 * than it saves.
 */
export function App(): React.JSX.Element {
  const [ready, setReady] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [tab, setTab] = useState<Tab>('rounds');
  const [openRound, setOpenRound] = useState<string | null>(null);

  // Try to restore a session before painting anything, so a returning user does
  // not see a login form flash past on every cold start.
  useEffect(() => {
    void api.restore().then((ok) => {
      setSignedIn(ok || isSignedIn());
      setReady(true);
    });
  }, []);

  const onSignedIn = useCallback((_session: Session) => {
    setSignedIn(true);
    setTab('rounds');
  }, []);

  const signOut = useCallback(async () => {
    await api.logout();
    storeSession(null);
    setSignedIn(false);
  }, []);

  if (!ready) {
    return (
      <div
        style={{
          display: 'grid',
          placeItems: 'center',
          minHeight: '100dvh',
          color: 'var(--muted)',
        }}
      >
        Inapakia…
      </div>
    );
  }

  if (!signedIn) return <Login onSignedIn={onSignedIn} />;

  if (openRound !== null) {
    return <RoundScreen roundId={openRound} onBack={() => setOpenRound(null)} />;
  }

  return (
    <>
      {tab === 'rounds' && <RoundList onSelect={setOpenRound} />}
      {tab === 'tickets' && <MyTickets />}
      {tab === 'wallet' && <Wallet />}
      {tab === 'account' && <Account onSignOut={() => void signOut()} />}

      <nav style={S.tabs}>
        {(
          [
            ['rounds', 'Raundi'],
            ['tickets', 'Tiketi'],
            ['wallet', 'Pochi'],
            ['account', 'Akaunti'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            style={tab === key ? S.tabActive : S.tab}
            aria-current={tab === key ? 'page' : undefined}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </nav>
    </>
  );
}

function RoundList({ onSelect }: { onSelect: (id: string) => void }): React.JSX.Element {
  const [rounds, setRounds] = useState<readonly RoundSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const data = await api.listRounds();
        if (!cancelled) {
          setRounds(data.rounds);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Imeshindikana kupakia.');
      }
    };
    void load();
    // The pool figure ticks up as tickets sell (spec §7). Ten seconds feels
    // live without putting a cheap phone's radio under constant load.
    const id = setInterval(() => void load(), 10_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  if (error !== null && rounds === null) {
    return (
      <main style={S.page}>
        <p style={S.error}>{error}</p>
      </main>
    );
  }
  if (rounds === null) {
    return (
      <main style={S.page}>
        <p style={S.muted}>Inapakia…</p>
      </main>
    );
  }

  return (
    <main style={S.page}>
      <h1 style={S.h1}>Raundi zinazoendelea</h1>
      {rounds.length === 0 && <p style={S.muted}>Hakuna raundi kwa sasa. Rudi hivi karibuni.</p>}

      <div style={{ display: 'grid', gap: 10 }}>
        {rounds.map((r) => (
          <button
            key={r.roundId}
            type="button"
            onClick={() => onSelect(r.roundId)}
            style={{ ...S.card, textAlign: 'left', cursor: 'pointer', color: 'var(--text)' }}
          >
            <div style={{ ...S.row, ...S.muted, fontSize: 13 }}>
              <span>
                {r.tierId} · mechi {r.legs}
              </span>
              <Countdown targetIso={r.entryCutoffAt} />
            </div>
            <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--accent)' }}>
              {formatTzs(r.prizePoolSenti)} TZS
            </div>
            {/* Progressive, always labelled. Never a static advertised figure. */}
            <div style={{ ...S.muted, fontSize: 12 }}>
              Jackpot sasa · inaongezeka · tiketi {r.ticketCount}
            </div>
          </button>
        ))}
      </div>
    </main>
  );
}

function RoundScreen({
  roundId,
  onBack,
}: {
  roundId: string;
  onBack: () => void;
}): React.JSX.Element {
  const [round, setRound] = useState<Awaited<ReturnType<typeof api.getRound>> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .getRound(roundId)
      .then((r) => {
        if (!cancelled) setRound(r);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Imeshindikana.');
      });
    return () => {
      cancelled = true;
    };
  }, [roundId]);

  return (
    <>
      <button type="button" onClick={onBack} style={{ ...S.buttonGhost, padding: 16 }}>
        ← Rudi
      </button>
      {error !== null && <p style={{ ...S.error, padding: 16 }}>{error}</p>}
      {round !== null && <Betslip round={round} />}
    </>
  );
}

function Account({ onSignOut }: { onSignOut: () => void }): React.JSX.Element {
  const [me, setMe] = useState<{ phone: string; selfExcludedUntil: string | null } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    void api
      .me()
      .then(setMe)
      .catch(() => undefined);
  }, []);

  const exclude = async (days: number): Promise<void> => {
    // Deliberate friction on an irreversible action. The confirm text says what
    // it actually does, including that it cannot be undone.
    const confirmed = window.confirm(
      `Utazuiwa kucheza kwa siku ${days}. Hii haiwezi kutenguliwa kabla ya muda kuisha. Endelea?`,
    );
    if (!confirmed) return;
    const r = await api.selfExclude(days);
    setNotice(`Umejizuia hadi ${new Date(r.selfExcludedUntil).toLocaleDateString()}.`);
  };

  return (
    <main style={S.page}>
      <h1 style={S.h1}>Akaunti</h1>

      <section style={{ ...S.card, marginBottom: 16 }}>
        <div style={S.muted}>Namba ya simu</div>
        <div style={{ fontSize: 18 }}>{me?.phone ?? '…'}</div>
      </section>

      <section style={{ ...S.card, marginBottom: 16 }}>
        <h2 style={S.h2}>Kucheza kwa uwajibikaji</h2>
        <p style={{ ...S.muted, marginTop: 0 }}>
          Ukihisi kucheza kunakuathiri, unaweza kujizuia. Hii inasimamisha ununuzi wote mara moja.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
          {[7, 30, 180].map((days) => (
            <button key={days} type="button" style={S.button} onClick={() => void exclude(days)}>
              Siku {days}
            </button>
          ))}
        </div>
        {notice !== null && <p style={S.success}>{notice}</p>}
      </section>

      <button type="button" style={{ ...S.button, width: '100%' }} onClick={onSignOut}>
        Toka
      </button>

      <p style={{ ...S.muted, marginTop: 24, lineHeight: 1.5 }}>
        Kucheza ni kwa wenye miaka 18 na zaidi. Cheza kwa kiasi.
      </p>
    </main>
  );
}

function Countdown({ targetIso }: { targetIso: string }): React.JSX.Element {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const seconds = Math.max(0, Math.floor((Date.parse(targetIso) - now) / 1000));
  return <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCountdown(seconds)}</span>;
}
